import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { auth } from '../services/firebase';

export async function checkSessionBreakdown() {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    console.error('❌ Not logged in');
    return;
  }
  
  const sessionsRef = collection(db, 'users', currentUser.uid, 'sessions');
  const snapshot = await getDocs(sessionsRef);
  
  let total = 0;
  let valid = 0;
  let invalid = 0;
  let lowAcc = 0;
  let abandoned = 0;
  
  snapshot.forEach(doc => {
    const d = doc.data();
    total++;
    if (d.isValid === false) {
      invalid++;
      if (d.abandonmentReason === 'low_accuracy') lowAcc++;
      if (d.abandonmentReason === 'inactivity_timeout') abandoned++;
    } else {
      valid++;
    }
  });
  
  console.log(`\n📊 SESSION BREAKDOWN for ${currentUser.email}:`);
  console.log(`   Total sessions: ${total}`);
  console.log(`   ✅ Valid sessions (accuracy ≥ 85%): ${valid}`);
  console.log(`   ❌ Invalid sessions: ${invalid}`);
  console.log(`   📉 Low accuracy (<85%): ${lowAcc}`);
  console.log(`   ⏰ Abandoned (timeout): ${abandoned}`);
  console.log(`\n💡 Analytics only shows the ${valid} valid sessions`);
  
  return { total, valid, invalid, lowAcc, abandoned };
}

export async function checkConsentSettings() {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    console.error('❌ Not logged in');
    return;
  }
  
  const { doc, getDoc } = await import('firebase/firestore');
  const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
  
  if (!userDoc.exists()) {
    console.error('❌ User document not found');
    return;
  }
  
  const data = userDoc.data();
  
  console.log(`\n🔍 CONSENT SETTINGS for ${data.username}:`);
  console.log(`   consentToShareImprovementScore: ${data.consentToShareImprovementScore || false}`);
  console.log(`   consentToDisplaySetupData: ${data.consentToDisplaySetupData || false}`);
  console.log(`   consentToDisplaySocialLinks: ${data.consentToDisplaySocialLinks || false}`);
  console.log(`   initialBaselineWPM: ${data.initialBaselineWPM || 'NOT SET'}`);
  
  if (!data.consentToShareImprovementScore) {
    console.log('\n⚠️  Leaderboard consent is OFF - user will not appear on leaderboard');
    console.log('   Enable in: Settings → Privacy Preferences → Share My Improvement Score');
  } else {
    console.log('\n✅ Leaderboard consent is ON - user should appear on leaderboard');
  }
  
  return {
    consentToShareImprovementScore: data.consentToShareImprovementScore || false,
    consentToDisplaySetupData: data.consentToDisplaySetupData || false,
    consentToDisplaySocialLinks: data.consentToDisplaySocialLinks || false,
    initialBaselineWPM: data.initialBaselineWPM || 0
  };
}

export async function testLeaderboard(rollingDays = 7) {
  console.log(`\n🔍 Testing leaderboard for ${rollingDays} days...`);
  
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const getLeaderboard = httpsCallable(functions, 'getImprovementLeaderboard');
    
    const result = await getLeaderboard({ rollingDays, maxResults: 50 });
    
    console.log('🔍 Raw result:', result);
    console.log('🔍 Result.data:', result.data);
    
    // Cloud Function returns { success: true, data: [...] }
    const responseData = result.data as any;
    const data = Array.isArray(responseData.data) ? responseData.data : [];
    
    console.log(`\n✅ Found ${data.length} users on leaderboard:`);
    data.forEach((entry: any, i: number) => {
      console.log(`   ${i + 1}. ${entry.username}: ${entry.improvementPercentage}% improvement (${entry.currentWPM} WPM)`);
    });
    
    if (data.length === 0) {
      console.log('\n⚠️  No users found! Possible reasons:');
      console.log('   1. No users have consentToShareImprovementScore = true');
      console.log('   2. No users have initialBaselineWPM set');
      console.log('   3. No users have sessions in the last ' + rollingDays + ' days');
    }
    
    return data;
  } catch (error) {
    console.error('❌ Error calling leaderboard function:', error);
    throw error;
  }
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).checkSessionBreakdown = checkSessionBreakdown;
  (window as any).checkConsentSettings = checkConsentSettings;
  (window as any).testLeaderboard = testLeaderboard;
}
